import React, { useState, useEffect } from 'react';
import {
  Mail,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  RotateCw,
  Clock,
  AlertCircle,
  Loader2,
  ExternalLink,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import lettersApi, { LetterSend } from '@/services/lettersApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SendStatus = LetterSend['status'];

interface MailDeliveryTrackerProps {
  sendId: number;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Timeline step definitions
// ---------------------------------------------------------------------------

interface TimelineStep {
  key: SendStatus;
  label: string;
  icon: React.ReactNode;
  timestampField: keyof LetterSend;
}

const TIMELINE_STEPS: TimelineStep[] = [
  { key: 'created', label: 'Created', icon: <Clock className="h-4 w-4" />, timestampField: 'created_at' },
  { key: 'processing', label: 'Processing', icon: <Package className="h-4 w-4" />, timestampField: 'sent_at' },
  { key: 'mailed', label: 'Mailed', icon: <Truck className="h-4 w-4" />, timestampField: 'sent_at' },
  { key: 'delivered', label: 'Delivered', icon: <CheckCircle className="h-4 w-4" />, timestampField: 'delivered_at' },
];

const STATUS_ORDER: SendStatus[] = ['created', 'processing', 'mailed', 'delivered'];

const TERMINAL_STATUSES: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  returned: {
    label: 'Returned',
    icon: <AlertCircle className="h-4 w-4" />,
    color: 'text-orange-600 bg-orange-50 border-orange-300',
  },
  failed: {
    label: 'Failed',
    icon: <XCircle className="h-4 w-4" />,
    color: 'text-red-600 bg-red-50 border-red-300',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateTime(dateStr?: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStepState(
  stepKey: SendStatus,
  currentStatus: SendStatus
): 'completed' | 'current' | 'upcoming' | 'failed' {
  const isTerminal = currentStatus === 'returned' || currentStatus === 'failed';
  const currentIdx = STATUS_ORDER.indexOf(isTerminal ? 'mailed' : currentStatus);
  const stepIdx = STATUS_ORDER.indexOf(stepKey);

  if (stepIdx < 0) return 'upcoming';

  if (isTerminal && stepIdx <= currentIdx) {
    return stepIdx === currentIdx ? 'current' : 'completed';
  }

  if (stepIdx < currentIdx) return 'completed';
  if (stepIdx === currentIdx) return 'current';
  return 'upcoming';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MailDeliveryTracker: React.FC<MailDeliveryTrackerProps> = ({
  sendId,
  onClose,
}) => {
  const [send, setSend] = useState<LetterSend | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const fetchSend = async () => {
    try {
      setLoading(true);
      const data = await lettersApi.getSend(sendId);
      setSend(data);
    } catch (err) {
      console.error('Failed to load send details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const updated = await lettersApi.refreshSendStatus(sendId);
      setSend(updated);
    } catch (err) {
      console.error('Failed to refresh status:', err);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSend();
  }, [sendId]);

  // -----------------------------------------------------------------------
  // Timeline rendering
  // -----------------------------------------------------------------------

  const isTerminalStatus = send && (send.status === 'returned' || send.status === 'failed');

  function renderTimeline() {
    if (!send) return null;

    const steps = [...TIMELINE_STEPS];

    // If the send ended in a terminal status, replace the last step
    if (isTerminalStatus) {
      const terminal = TERMINAL_STATUSES[send.status];
      steps[steps.length - 1] = {
        key: send.status,
        label: terminal.label,
        icon: terminal.icon,
        timestampField: send.status === 'returned' ? 'returned_at' : 'created_at',
      };
    }

    return (
      <div className="relative ml-4 mt-2">
        {steps.map((step, idx) => {
          const state = getStepState(
            isTerminalStatus && idx === steps.length - 1 ? 'delivered' : step.key,
            send.status
          );
          // For terminal status on last step, mark as failed
          const effectiveState =
            isTerminalStatus && idx === steps.length - 1 ? 'failed' : state;

          const timestamp = send[step.timestampField] as string | undefined;
          const isLast = idx === steps.length - 1;

          // Connector line color
          const lineColor =
            effectiveState === 'completed' || effectiveState === 'current'
              ? 'bg-green-400'
              : 'bg-gray-200';

          // Dot styles
          let dotClasses = 'flex h-8 w-8 items-center justify-center rounded-full border-2 ';
          let iconColor = '';
          switch (effectiveState) {
            case 'completed':
              dotClasses += 'border-green-500 bg-green-50 text-green-600';
              iconColor = 'text-green-600';
              break;
            case 'current':
              dotClasses += 'border-blue-500 bg-blue-50 text-blue-600 ring-2 ring-blue-200';
              iconColor = 'text-blue-600';
              break;
            case 'failed':
              dotClasses +=
                send.status === 'returned'
                  ? 'border-orange-500 bg-orange-50 text-orange-600 ring-2 ring-orange-200'
                  : 'border-red-500 bg-red-50 text-red-600 ring-2 ring-red-200';
              iconColor =
                send.status === 'returned' ? 'text-orange-600' : 'text-red-600';
              break;
            default:
              dotClasses += 'border-gray-300 bg-gray-50 text-gray-400';
              iconColor = 'text-gray-400';
              break;
          }

          return (
            <div key={step.key} className="relative flex gap-4 pb-8 last:pb-0">
              {/* Vertical connector */}
              {!isLast && (
                <div
                  className={`absolute left-[15px] top-8 h-full w-0.5 ${lineColor}`}
                />
              )}

              {/* Dot */}
              <div className={`relative z-10 shrink-0 ${dotClasses}`}>
                <span className={iconColor}>{step.icon}</span>
              </div>

              {/* Label & timestamp */}
              <div className="flex flex-col justify-center pt-0.5">
                <span className="text-sm font-medium">{step.label}</span>
                {timestamp ? (
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(timestamp)}
                  </span>
                ) : effectiveState === 'upcoming' ? (
                  <span className="text-xs text-muted-foreground">Pending</span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Mail Delivery Tracker
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="mr-2 h-5 w-5 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Loading details...</span>
          </div>
        ) : !send ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <AlertCircle className="mb-2 h-8 w-8 opacity-40" />
            <p className="text-sm">Send record not found.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {/* Recipient info */}
            <Card>
              <CardContent className="pt-4 space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <MapPin className="mt-0.5 h-4 w-4 text-muted-foreground shrink-0" />
                  <div>
                    <div className="font-medium">{send.recipient_name || 'Unknown Recipient'}</div>
                    {send.recipient_address_line1 && (
                      <div className="text-muted-foreground">{send.recipient_address_line1}</div>
                    )}
                    {send.recipient_city && send.recipient_state && send.recipient_zip && (
                      <div className="text-muted-foreground">
                        {send.recipient_city}, {send.recipient_state} {send.recipient_zip}
                      </div>
                    )}
                    {send.recipient_address && !send.recipient_address_line1 && (
                      <div className="text-muted-foreground">{send.recipient_address}</div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pt-2 border-t">
                  <div>
                    <span className="text-xs text-muted-foreground">Template</span>
                    <div className="font-medium">{send.template_name || '-'}</div>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground">Mail Type</span>
                    <div className="font-medium">
                      {send.mail_type?.replace('usps_', '').replace('_', ' ') || '-'}
                    </div>
                  </div>
                  {send.expected_delivery_date && (
                    <div>
                      <span className="text-xs text-muted-foreground">Expected Delivery</span>
                      <div className="font-medium">{formatDate(send.expected_delivery_date)}</div>
                    </div>
                  )}
                  {send.tracking_url && (
                    <div>
                      <span className="text-xs text-muted-foreground">Tracking</span>
                      <div>
                        <a
                          href={send.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
                        >
                          View tracking
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>

                {send.error_message && (
                  <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                    {send.error_message}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Delivery timeline */}
            <div>
              <h4 className="mb-2 text-sm font-semibold">Delivery Timeline</h4>
              {renderTimeline()}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                {refreshing ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RotateCw className="mr-1.5 h-3.5 w-3.5" />
                )}
                Refresh Status
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MailDeliveryTracker;
